// frontend/src/components/asset/ReturnToInventoryModal.js
/**
 * Custodian/HR immediate return-to-inventory (Compose modal).
 *
 * Used by:
 *   - /asset-admin/assets
 *   - /admin/asset-inventory
 *
 * Asset-type capability controls physical-handover fields (condition, photos,
 * shelf notes). Project stays for digital entitlements that are costed to jobs.
 */
import React, { useEffect, useState } from 'react';
import { FileKey, ShieldOff, UserMinus } from 'lucide-react';
import toast from '../../utils/toast';
import {
  isSoftwareLicenseAsset,
  requiresPhysicalHandover,
  usesAccessDeactivationWorkflow,
} from '../../utils/assetAccess';
import { formatDate, getTodayInSingaporeTimezone, parseLocalDate, toLocalISODate } from '../../utils/dateUtils';
import Modal from '../UI/Modal';
import FormSelect from '../UI/FormSelect';
import DatePickerField from '../UI/DatePickerField';
import ProjectSiteSelector from '../ProjectSiteSelector';
import { ReturnPhotoUploader, validateReturnPhotosForSubmit } from './PendingReturnsShared';

const returnDateHint = (assignedOn, today) => {
  if (assignedOn && assignedOn === today) {
    return `Assigned today, so the return date is ${formatDate(assignedOn)}.`;
  }
  if (assignedOn) {
    return `The day this person stopped holding it. Defaults to today — choose an earlier day if the handover already happened. Earliest is ${formatDate(assignedOn)}, when it was assigned.`;
  }
  return 'The day this person stopped holding it. Defaults to today — choose an earlier day if the handover already happened.';
};

const CONDITION_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' },
  { value: 'faulty', label: 'Faulty' },
];

const ReturnToInventoryModal = ({
  isOpen,
  onClose,
  asset,
  onSubmit,
  isLoading,
  title = 'Return to Inventory',
  submitLabel = 'Return to Inventory',
  submittingLabel = 'Processing...',
}) => {
  const isSoftware = isSoftwareLicenseAsset(asset);
  const isPhysicalHandover = requiresPhysicalHandover(asset);
  const usesDeactivation = usesAccessDeactivationWorkflow(asset);
  const today = getTodayInSingaporeTimezone();
  const assignedOn = asset?.assigned_date
    ? String(asset.assigned_date).slice(0, 10)
    : '';
  const minReturnDate = assignedOn && assignedOn <= today ? assignedOn : '';
  const [effectiveDate, setEffectiveDate] = useState(today);
  const [condition, setCondition] = useState('good');
  const [projectCode, setProjectCode] = useState('');
  const [workSiteId, setWorkSiteId] = useState(null);
  const [locationNotes, setLocationNotes] = useState('');
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState([]);
  const [isOffboarding, setIsOffboarding] = useState(false);

  useEffect(() => {
    if (isOpen && asset) {
      setEffectiveDate(getTodayInSingaporeTimezone());
      setProjectCode(asset.deployed_project_code || '');
      setWorkSiteId(asset.deployed_work_site_id || null);
      setLocationNotes('');
      setNotes('');
      setCondition('good');
      setPhotos([]);
      setIsOffboarding(false);
    }
  }, [isOpen, asset]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (usesDeactivation) {
      toast.error('Use Deactivate Access for this digital credential.');
      return;
    }
    if (!effectiveDate) {
      toast.error('Date of return is required');
      return;
    }
    if (effectiveDate > today) {
      toast.error('Date of return cannot be in the future');
      return;
    }
    if (minReturnDate && effectiveDate < minReturnDate) {
      toast.error('Date of return cannot be before the asset was assigned');
      return;
    }
    if (isPhysicalHandover) {
      const photoError = validateReturnPhotosForSubmit(photos);
      if (photoError) {
        toast.error(photoError);
        return;
      }
      if (isOffboarding && photos.length === 0) {
        toast.error('At least one condition photo is required for offboarding collections.');
        return;
      }
    }
    onSubmit({
      asset_id: asset.id,
      effective_date: effectiveDate,
      ...(isPhysicalHandover ? { condition, location: locationNotes } : {}),
      deployed_project_code: projectCode,
      deployed_work_site_id: workSiteId,
      notes,
      is_offboarding: isOffboarding,
      photos: isPhysicalHandover && photos.length > 0 ? photos : undefined,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      theme="admin"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-admin-outline" disabled={isLoading}>
            Cancel
          </button>
          <button
            type="submit"
            form="return-to-inventory-form"
            disabled={isLoading || usesDeactivation}
            className="btn btn-warning"
          >
            <UserMinus className="w-4 h-4 mr-2" />
            {isLoading ? submittingLabel : submitLabel}
          </button>
        </>
      }
    >
      <form id="return-to-inventory-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-yellow-50 rounded-lg p-3">
          <p className="text-sm font-medium text-yellow-800">{asset?.asset_tag}</p>
          <p className="text-xs text-yellow-600">
            Currently with: {asset?.current_assignee_name || 'Unknown'}
            {assignedOn ? ` · since ${formatDate(assignedOn)}` : ''}
          </p>
        </div>

        {!isPhysicalHandover && (
          <div className="flex items-start gap-2 rounded-lg bg-purple-50 p-3">
            {usesDeactivation ? (
              <ShieldOff className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
            ) : (
              <FileKey className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
            )}
            <p className="text-sm text-purple-800">
              {usesDeactivation
                ? 'Digital access credential — use Deactivate Access instead of returning it to inventory.'
                : `${isSoftware ? 'Software seat' : 'Digital entitlement'} — returning it reclaims the entitlement. Condition, photos, and storage location do not apply.`}
            </p>
          </div>
        )}

        {!usesDeactivation && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="return-effective-date">
              Date of return <span className="text-red-500">*</span>
            </label>
            <DatePickerField
              id="return-effective-date"
              theme="admin"
              selected={parseLocalDate(effectiveDate)}
              onChange={(date) => setEffectiveDate(date ? toLocalISODate(date) : '')}
              minDate={minReturnDate ? parseLocalDate(minReturnDate) : undefined}
              maxDate={parseLocalDate(today)}
              placeholderText="Select date of return"
            />
            <p className="text-xs text-gray-500 mt-1">
              {returnDateHint(minReturnDate, today)}
            </p>
          </div>
        )}

        {isPhysicalHandover && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Condition at Return</label>
            <FormSelect
              value={condition}
              onChange={setCondition}
              options={CONDITION_OPTIONS}
              theme="admin"
            />
          </div>
        )}

        <ProjectSiteSelector
          projectCode={projectCode}
          workSiteId={workSiteId}
          onProjectChange={setProjectCode}
          onWorkSiteChange={setWorkSiteId}
          locationNotes={locationNotes}
          onLocationNotesChange={setLocationNotes}
          showNotes={isPhysicalHandover}
          compact
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="return-notes">
            Notes
          </label>
          <input
            id="return-notes"
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={
              !isPhysicalHandover
                ? 'Revocation notes, vendor account, follow-up…'
                : 'Condition notes, damage report, etc.'
            }
            className="input-admin w-full"
          />
        </div>

        {!usesDeactivation && (
          <label className="flex items-start gap-2 cursor-pointer bg-gray-50 rounded-lg p-3">
          <input
            type="checkbox"
            checked={isOffboarding}
            onChange={(e) => setIsOffboarding(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 accent-red-600 text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
          />
          <span className="text-sm text-gray-700">
            This is an <span className="font-medium">offboarding collection</span>
            <span className="block text-xs text-gray-500">
              {!isPhysicalHandover
                ? 'Records the return as an offboarding collection. No photo is required for digital entitlements.'
                : 'Records the return as an offboarding collection and requires a condition photo.'}
            </span>
          </span>
          </label>
        )}

        {isPhysicalHandover && (
          <ReturnPhotoUploader
            photos={photos}
            onChange={setPhotos}
            label={isOffboarding ? 'Condition Photos (required)' : 'Condition Photos (optional)'}
            helpText="Document the asset's condition at handover. Up to 5 images, 5MB each. JPEG, PNG, WebP."
          />
        )}
      </form>
    </Modal>
  );
};

export default ReturnToInventoryModal;
